import { Modal } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomerInfo } from '@common/models/customer-info.model';
import CustomerDataForm from '@core/components/CustomerDataModal/CustomerDataForm';
import useZipcodeLocation from '@ee-api/zipcode-api/useZipcodeLocation';
import { useAppSelector } from '@redux/hooks';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import './CustomerDataModal.scss';

interface CustomerDataModalProps {
    visible?: boolean;
    onCancel?: () => void;
    onSave: (customerInfo: CustomerInfo) => void;
    isSavingEstimate: boolean;
    initialCustomerData: CustomerInfo;
    isFetchedCustomerData: boolean;
    showSendEmailOption?: boolean;
}

/**
 * @param onCancel
 * @param onSave function(customerData) will receive the updated customerData
 * @param initialCustomerData initial data to display
 * @param isFetchedCustomerData disables email editing
 * @param isSavingEstimate true will diable entry and show spinner on button
 * @param showRemember show "remember me" options?
 * @param visible is the modal visible
 */
const CustomerDataModal = ({
    onCancel,
    onSave,
    initialCustomerData,
    isFetchedCustomerData,
    isSavingEstimate,
    visible,
    showSendEmailOption,
}: CustomerDataModalProps): JSX.Element => {
    const { t } = useTranslation();
    const selectedZipCode = useAppSelector(selectZipcode);
    const { zipcodeLocation } = useZipcodeLocation();

    // if we're missing primary phone but have alternate, swap them in this data to fix it
    const swapPhones = !initialCustomerData.phone.length && initialCustomerData.alternatePhone.length > 0;

    const initialData = useMemo(
        () => ({
            ...initialCustomerData,
            ...(!isFetchedCustomerData && {
                city: zipcodeLocation?.city ?? '',
                state: zipcodeLocation?.state ?? '',
                zip: selectedZipCode ?? '',
            }),
            phone: swapPhones ? initialCustomerData.alternatePhone : initialCustomerData.phone,
            alternatePhone: swapPhones ? '' : initialCustomerData.alternatePhone,
        }),
        [
            initialCustomerData,
            isFetchedCustomerData,
            swapPhones,
            zipcodeLocation?.city,
            zipcodeLocation?.state,
            selectedZipCode,
        ]
    );

    if (!visible) {
        return <></>;
    }

    return (
        <Modal
            centered
            destroyOnClose
            open={visible}
            closable={false}
            maskClosable
            footer={null}
            wrapProps={{ span: 24 }}
        >
            <div className="CustomerDataModal__title">{t('SAVE.TITLE')}</div>
            <div className="CustomerDataModal__subtitle">{t('SAVE.SUBTITLE')}</div>
            <CustomerDataForm
                onCancel={onCancel}
                onSave={onSave}
                isSavingEstimate={isSavingEstimate}
                initialCustomerData={initialData}
                showSendEmailOption={showSendEmailOption}
            />
        </Modal>
    );
};

export default CustomerDataModal;
